import { type FC, memo, useState, useEffect, useRef } from "react";
import translate from "src/lib/translate";
import type { FileRef } from "@9amhealth/shared";
import {
  CenterEllipsisText,
  DocumentIcon,
  fileSizeString
} from "@9amhealth/shared";
import FilesCubit from "src/state/FilesCubit/FilesCubit";
import { fileState } from "src/state/state";
import {
  MessageFilesWrap,
  MessageFilesGrid,
  MessageFileCell,
  MessageFileCaption,
  MessageFileFallback,
  MessageFileSpinner
} from "./MessageFiles.styled";

const PREVIEW_SIZE = 300;
const MAX_GRID_COUNT = 4;

interface MessageFilesProps {
  files: FileRef[];
  variant?: "bubble" | "card";
  downloadSource?: string;
}

type PreviewPhase = "idle" | "loading" | "settled";

const MessageFiles: FC<MessageFilesProps> = memo(
  ({ files, variant = "bubble", downloadSource = "chat-message" }) => {
    const [previews, setPreviews] = useState<Record<string, string>>({});
    const [phase, setPhase] = useState<PreviewPhase>("idle");
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const el = wrapRef.current;
      if (!el) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setPhase("loading");
            observer.disconnect();
          }
        },
        { rootMargin: "200px" }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      if (phase !== "loading") {
        return;
      }

      let cancelled = false;
      let pending = files.length;

      for (const file of files) {
        fileState
          .loadPreview(file.fileId, PREVIEW_SIZE, PREVIEW_SIZE)
          .then((url) => {
            if (!cancelled && url) {
              setPreviews((prev) => ({ ...prev, [file.fileId]: url }));
            }
          })
          .catch(() => {})
          .finally(() => {
            pending--;
            if (!cancelled && pending <= 0) {
              setPhase("settled");
            }
          });
      }

      return () => {
        cancelled = true;
      };
    }, [phase, files]);

    const count = files.length;

    return (
      <MessageFilesWrap
        data-message-files=""
        data-variant={variant}
        ref={wrapRef}
      >
        <MessageFilesGrid data-count={Math.min(count, MAX_GRID_COUNT)}>
          {files.map((f) => (
            <MessageFileCell
              key={f.fileId}
              onClick={() =>
                void FilesCubit.startFileDownload(f.fileId, downloadSource)
              }
            >
              {previews[f.fileId] ? (
                <>
                  <img
                    src={previews[f.fileId]}
                    alt={f.name ?? translate("leapChat.file.fallbackName")}
                  />
                  {f.name && (
                    <MessageFileCaption>
                      <CenterEllipsisText
                        className="caption-name"
                        text={f.name}
                      />
                    </MessageFileCaption>
                  )}
                </>
              ) : phase === "loading" ? (
                <MessageFileSpinner />
              ) : (
                <MessageFileFallback>
                  <DocumentIcon />
                  <CenterEllipsisText
                    className="fallback-name"
                    text={f.name ?? translate("leapChat.file.fallbackName")}
                  />
                  {f.size != null && (
                    <span className="fallback-size">
                      {fileSizeString(f.size)}
                    </span>
                  )}
                </MessageFileFallback>
              )}
            </MessageFileCell>
          ))}
        </MessageFilesGrid>
      </MessageFilesWrap>
    );
  }
);

export default MessageFiles;
